import React, { forwardRef, useEffect, useState } from 'react';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  EIconName,
  NumberInput,
  SwitchElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './per-minute-item.scss';

// Utils
import { numToFixed } from '../../../../../../utils/math';
import { replaceCurrencySymbol } from '../../../../../../utils/currency';

// Models
import { ICurrency } from '../../../../../../interfaces/company.interfaces';
import { TPPMType } from '../../../../../../interfaces/price-settings.interfaces';

export type TPerMinuteItemRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TPerMinuteItemProps = {
  type: TPPMType;
  initialRate: string;
  currency: ICurrency;
  hourRate: number;
  initialProfit: string;
  hourProfit: number;
  serviceFee: number;
  serviceEnabled: boolean;
  savingChanges: boolean;
  hasChanges: boolean;
  availableRates: boolean;
  toggleEditMode: (type: TPPMType, checked: boolean, event: any) => void;
  saveChanges: (type: TPPMType) => void;
  disabledBlock?: boolean;
};

export const PerMinuteItem = forwardRef<TPerMinuteItemRef, TPerMinuteItemProps>((
  {
    type = 'audio_call_rate',
    initialRate,
    currency,
    hourRate,
    initialProfit,
    hourProfit,
    serviceFee,
    serviceEnabled,
    savingChanges,
    hasChanges,
    availableRates,
    toggleEditMode,
    saveChanges,
    disabledBlock = false
  }: TPerMinuteItemProps, ref
) => {

  const [switchChecked, setSwitchChecked] = useState<boolean>(availableRates);

  const parserHandler = (value: string | undefined): number => {
    return Number(replaceCurrencySymbol(value || '', currency.symbol));
  }

  const getIconOfType = (): EIconName => {
    if (type === 'call_rate') {
      return EIconName.CameraOn;
    }
    if (type === 'audio_call_rate') {
      return EIconName.MicOn;
    }
    return EIconName.Chat;
  }

  useEffect(() => {
    setSwitchChecked(availableRates);
  }, [availableRates]);

  return (
    <div className="price-per-minute--edit-price">
      <div className="edit-price-col edit-price-col--type">
        <div className={ `col-header col-header--${ type }` }/>
        <div className="col-body">
          <CustomIcon name={ getIconOfType() } size={ 'size-xll' }/>
          <span className="col-text">
            { type === 'call_rate' && 'Video Consultation' }
            { type === 'audio_call_rate' && 'Voice Consultation' }
            { type === 'conversation_rate' && 'Text Chat' }
          </span>
          <SwitchElement disabled={ disabledBlock } checked={ switchChecked }
                         onChange={ (checked, event) => toggleEditMode(type, checked, event) }/>
        </div>
      </div>

      <div className="edit-price-col">
        <div className={ `col-header col-header--${ type }` }>
          Payable Amount for Clients per min <CustomTooltip
          title={ 'The amount covers both the Service Price and the Credit Card Processing Fee, with no hidden or extra charges' }><CustomIcon
          name={ EIconName.Info }
          size={ 'size-md' }/></CustomTooltip>
        </div>
        <div className="col-body">
          <Form.Item name={ type } initialValue={ initialRate }>
            <NumberInput disabled={ (!serviceEnabled && !availableRates) || savingChanges || disabledBlock }
                         min={ 0.21 }
                         max={ 10000 }
                         placeholder={ 'Rate' }
                         precision={ 2 }
                         formatter={ (value) => `${ currency.symbol }${ value }` }
                         parser={ parserHandler }
                         controls={ false }/>
          </Form.Item>
          /min
          <div className="col-body--hint">
            { currency.symbol }{ numToFixed(hourRate * 60) } /hr
          </div>
        </div>
      </div>

      <div className="edit-price-col">
        <div className={ `col-header col-header--${ type }` }/>
        <div className="col-body col-body--equals">
          =
        </div>
      </div>

      <div className="edit-price-col">
        <div className={ `col-header col-header--${ type }` }>
          Credit Card Processing Fee <CustomTooltip
          title={ 'Fee charged by the Payment System for processing credit card transactions' }>
          <CustomIcon name={ EIconName.Info } size={ 'size-md' }/></CustomTooltip>
        </div>
        <div className="col-body">
          { currency.symbol }{ serviceFee } /min
        </div>
      </div>

      <div className="edit-price-col">
        <div className={ `col-header col-header--${ type }` }/>
        <div className="col-body col-body--plus">
          +
        </div>
      </div>

      <div className="edit-price-col">
        <div className={ `col-header col-header--${ type }` }>
          Service Price <CustomTooltip
          title={ 'The amount to be credited into your bank account for provided service to your Clients' }><CustomIcon
          name={ EIconName.Info }
          size={ 'size-md' }/></CustomTooltip>
        </div>
        <div className="col-body">
          <Form.Item name={ `profit_${ type }` } initialValue={ initialProfit }>
            <NumberInput disabled={ (!serviceEnabled && !availableRates) || savingChanges || disabledBlock }
                         placeholder={ 'Profit' }
                         min={ 0.2 }
                         max={ 10000 }
                         precision={ 2 }
                         formatter={ (value) => `${ currency.symbol }${ value }` }
                         parser={ parserHandler }
                         controls={ false }/>
          </Form.Item>
          /min
          <div className="col-body--hint">
            { currency.symbol }{ numToFixed(hourProfit * 60) } /hr
          </div>
        </div>
      </div>

      <div className="edit-price-col edit-price-col--action">
        <div className={ `col-header col-header--${ type }` }/>
        <div className="col-body" hidden={ !hasChanges || disabledBlock }>
          { availableRates && <CustomButton disabled={ savingChanges || disabledBlock }
                                            size={ 'small' }
                                            text={ 'Save' }
                                            onClick={ () => saveChanges(type) }/> }
        </div>
      </div>
    </div>
  );
})
